import * as React from 'react';
import { UserState, useUserState } from '../../state/UserState';
import styles from './styles.module.css';
import { TFunction, useTranslation } from 'react-i18next';
import { LeftMenuLink } from '../left-menu-link/left-menu-link';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BarChart from '@mui/icons-material/BarChart';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import VpnKey from '@mui/icons-material/VpnKey';
import { RolesEnum } from '../../domain/enums/roles.enum';
import { RenderIf } from '@lib/ui-components';
import {
  AllInbox,
  Assignment,
  CalendarMonthOutlined,
  CallReceived,
  ContactMail,
  FactCheck,
  Handshake,
  InfoOutlined,
  LocalShipping,
  Map,
  QuestionMark,
  SimCardDownload,
  Store,
} from '@mui/icons-material';
import axios from 'axios';
import { ProjectSettings } from '@lib/api-interface';

function technicalAdminMenu(t: TFunction<'common'>) {
  return (
    <div className={styles['menu']}>
      <LeftMenuLink to='organization-users' label={t('organizationUsers')} icon={<GroupIcon />} />
      <LeftMenuLink to='aggregates' label={t('settings')} icon={<SettingsIcon />} />
    </div>
  );
}

function organizationMenu(userState: UserState, t: TFunction<'common'>) {
  return (
    <div className={styles['menu']}>
      <LeftMenuLink to='projects' label={t('projects')} icon={<MenuBookIcon />} />
      <LeftMenuLink to='companies' label={t('companies')} icon={<PeopleIcon />} />
      <LeftMenuLink to={'maps'} label={t('maps')} icon={<Map />} />
      <LeftMenuLink to='collection-requests' label={t('reportsReport')} icon={<ArrowDownwardIcon />} />
      <LeftMenuLink to='communes' label={t('communes')} icon={<LocationCityIcon />} />
      <LeftMenuLink to='month-end-closing' label={t('monthEndClosing')} icon={<VpnKey />} />
      <LeftMenuLink to='reports' label={t('reports')} icon={<BarChart />} />
      <RenderIf true={userState.authorities.includes('ORGANIZATION_ADMIN')}>
        <LeftMenuLink to='global-settings' label={t('settings')} icon={<SettingsIcon />} />
      </RenderIf>
      <RenderIf true={userState.authorities.includes('ORGANIZATION_ADMIN') || userState.authorities.includes('MANAGES_USERS')}>
        <LeftMenuLink to='registers' label={t('registers')} icon={<FolderIcon />} />
      </RenderIf>
      <LeftMenuLink to='holidays-calendar' label={t('calendar')} icon={<CalendarMonthOutlined />} />
    </div>
  );
}

function subcontractorMenu(t: TFunction<'common'>) {
  return (
    <div className={styles['menu']}>
      <LeftMenuLink to='collection-requests' label={t('reportsV2')} icon={<ArrowDownwardIcon />} />
      <LeftMenuLink to='quantity-received' label={t('quantityReceived')} icon={<CallReceived />} />
      <LeftMenuLink to='today-pickups' label={t('todayPickups')} icon={<LocalShipping />} />
      <LeftMenuLink to='agreements' label={t('agreements')} icon={<Handshake />} />
      <LeftMenuLink to='decisions' label={t('decisions')} icon={<Assignment />} />
      <LeftMenuLink to='reports' label={t('reports')} icon={<BarChart />} />
      <LeftMenuLink to='contact-form' label={t('contactForm')} icon={<ContactMail />} />
    </div>
  );
}

function clientUserMenu(canGenerateReports: boolean, t: TFunction<'common'>) {
  return (
    <div className={styles['menu']}>
      <LeftMenuLink to='pickups' label={t('pickups')} icon={<LocalShipping />} />
      <LeftMenuLink to='infrastructure' label={t('infrastructure')} icon={<AllInbox />} />
      <LeftMenuLink to='contact-form' label={t('contactForm')} icon={<ContactMail />} />
      <LeftMenuLink to='documents' label={t('documents')} icon={<SimCardDownload />} />
      <RenderIf true={canGenerateReports}>
        <LeftMenuLink to='reports' label={t('reports')} icon={<BarChart />} />
      </RenderIf>
      <LeftMenuLink to='instruction' label={t('clientPoiInstruction')} icon={<QuestionMark />} />
    </div>
  );
}

function clientAdminMenu(userState: UserState, seePickups: boolean, canGenerateReports: boolean, t: TFunction<'common'>) {
  return (
    <div className={styles['menu']}>
      <LeftMenuLink to='services-to-accept' label={t('servicesToAccept')} icon={<FactCheck />} />
      <LeftMenuLink to='branches' label={t('branches')} icon={<Store />} />
      <RenderIf true={seePickups}>
        <LeftMenuLink to='pickups' label={t('pickups')} icon={<LocalShipping />} />
      </RenderIf>
      <LeftMenuLink to='collection-requests' label={t('reportsV2')} icon={<ArrowDownwardIcon />} />
      <RenderIf true={canGenerateReports}>
        <LeftMenuLink to='reports' label={t('reports')} icon={<BarChart />} />
      </RenderIf>
      <LeftMenuLink to='client-data' label={t('Companies.CompanyView.companyClientInfo')} icon={<InfoOutlined />} />
      <LeftMenuLink to='contact-form' label={t('contactForm')} icon={<ContactMail />} />
      <RenderIf true={userState.authorities.includes('ORGANIZATION_ADMIN')}>
        <LeftMenuLink to='global-settings' label={t('settings')} icon={<SettingsIcon />} />
      </RenderIf>
      <RenderIf true={userState.authorities.includes('ORGANIZATION_ADMIN') || userState.authorities.includes('MANAGES_USERS')}>
        <LeftMenuLink to='registers' label={t('registers')} icon={<FolderIcon />} />
      </RenderIf>
      <LeftMenuLink to='instruction' label={t('clientPoiInstruction')} icon={<QuestionMark />} />
    </div>
  );
}

export const LeftMenuItemsSelector = () => {
  const userState = useUserState();
  const { t } = useTranslation();
  const [projectSettings, setProjectSettings] = React.useState<ProjectSettings | null>(null);

  React.useEffect(() => {
    if (userState.roles.includes(RolesEnum.CLIENT_USER) || userState.roles.includes(RolesEnum.CLIENT_ADMIN)) {
      const fetchSettings = async () => {
        const url = `${process.env['REACT_APP_API_URL']}/api/project-branch-view/settings`;
        try {
          const response = await axios.get(url);
          setProjectSettings(response.data);
        } catch (error) {
          console.error('Failed to fetch settings:', error);
          setProjectSettings(null); // handle error appropriately
        }
      };

      fetchSettings();
    }
  }, []);

  if (!userState.isPresent || userState.roles.length === 0) {
    return <div />;
  }

  if (userState.roles.includes(RolesEnum.CLIENT_ADMIN)) {
    const canReportCollections = projectSettings?.userAdminCanReportCollections ?? false;
    const canGenerateReports = userState.roles.includes(RolesEnum.REGIONAL_ADMIN)
      ? projectSettings?.regionalAdminSeePricesAndCanGenerateReports ?? false
      : true;

    return clientAdminMenu(userState, canReportCollections, canGenerateReports, t);
  }

  switch (userState.roles[0]) {
    case RolesEnum.TECHNICAL_ADMIN:
      return technicalAdminMenu(t);
    case RolesEnum.ORGANIZATION:
      return organizationMenu(userState, t);
    case RolesEnum.CONTRACTOR:
      return subcontractorMenu(t);
    case RolesEnum.CLIENT_USER: {
      return clientUserMenu(projectSettings?.branchCanGenerateReports || false, t);
    }
    default:
      return <div />;
  }
};
